import React from 'react';
import { StyleSheet, View } from 'react-native';
import { BalanceCard, IllusCredit_2, theme, useResponsive } from '@sg/garnish';

import { GiftCard } from '@order/features/gift-card';
import { useLocalizationContext } from '@order/Localization';

import {
  GiftCardAccountSectionAddCardCta,
  GiftCardAccountSectionContainer,
  GiftCardAccountSectionGiftCardTotal,
} from './components';
import { useGiftCardBalance } from './hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const GiftCardAccountSection = (props: GiftCardAccountSectionProps) => {
  const { giftCardCode, giftCardRegistrationCode } = props;

  const { minWidth } = useResponsive();
  const { balance, isFetching } = useGiftCardBalance();

  // ─────────────────────────────────────────────────────────────────────

  if (minWidth.isSM) {
    return (
      <GiftCardAccountSectionSM
        giftCardCode={giftCardCode}
        giftCardRegistrationCode={giftCardRegistrationCode}
        giftCardBalance={balance}
        isFetching={isFetching}
      />
    );
  }

  return (
    <GiftCardAccountSectionXS
      giftCardBalance={balance}
      isFetching={isFetching}
    />
  );
};

// ─── Responsive Components ───────────────────────────────────────────────────

export const GiftCardAccountSectionXS = (
  props: Omit<
    GiftCardAccountSectionInnerProps,
    'giftCardCode' | 'giftCardRegistrationCode'
  >,
) => {
  const { isFetching, giftCardBalance } = props;

  return (
    <GiftCardAccountSectionContainer>
      <GiftCardAccountSectionGiftCardTotal
        totalAmount={giftCardBalance}
        isFetching={isFetching}
      />
      <GiftCardAccountSectionAddCardCta />
    </GiftCardAccountSectionContainer>
  );
};

export const GiftCardAccountSectionSM = (
  props: GiftCardAccountSectionInnerProps,
) => {
  const {
    giftCardCode,
    giftCardRegistrationCode,
    isFetching,
    giftCardBalance,
  } = props;

  const { t } = useLocalizationContext();

  // ─────────────────────────────────────────────────────────────────────

  return (
    <GiftCardAccountSectionContainer>
      <View style={styles.cardContainer}>
        <BalanceCard
          title={t('account.gift-cards.gift-card-total')}
          balance={giftCardBalance}
          illustrationSource={IllusCredit_2}
          isLoading={isFetching}
        />
      </View>

      <View style={styles.formContainer}>
        <GiftCard.RedemptionForm
          style={styles.withoutBorder}
          giftCardCode={giftCardCode}
          giftCardRegistrationCode={giftCardRegistrationCode}
        />
      </View>
    </GiftCardAccountSectionContainer>
  );
};

//

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  cardContainer: {
    alignItems: 'center',
    paddingTop: theme.spacing['6'],
    paddingBottom: theme.spacing['12'],
  },
  formContainer: {
    paddingBottom: theme.spacing['6'],
  },
  withoutBorder: {
    padding: 0,
    borderWidth: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type GiftCardAccountSectionProps = {
  giftCardCode?: string;
  giftCardRegistrationCode?: string;
};

type GiftCardAccountSectionInnerProps = {
  giftCardBalance: string;
  isFetching: boolean;
  giftCardCode?: string;
  giftCardRegistrationCode?: string;
};
